if (!customElements.get('product-form')) {
    const PRELOAD_LINK_DATA_ATTR = 'data-product-form-preload';
    const VARIANT_CACHE_MAX = 15;

    const requestIdle = (callback) => {
        if (typeof window.requestIdleCallback === 'function') {
            return window.requestIdleCallback(callback, { timeout: 1500 });
        }

        return window.setTimeout(callback, 200);
    };

    const preloadImage = (src, srcset, sizes) => {
        if (!src) return;

        const selector = `link[${PRELOAD_LINK_DATA_ATTR}="${CSS.escape(src)}"]`;
        if (document.head.querySelector(selector)) return;

        const elLink = document.createElement('link');
        elLink.rel = 'preload';
        elLink.as = 'image';
        elLink.href = src;
        if (srcset) elLink.setAttribute('imagesrcset', srcset);
        if (sizes) elLink.setAttribute('imagesizes', sizes);
        elLink.setAttribute(PRELOAD_LINK_DATA_ATTR, src);
        document.head.appendChild(elLink);
    };

    const preloadVariantImage = (elOptionValue) => {
        const { variantImageSrc, variantImageSrcset, variantImageSizes } = elOptionValue.dataset;

        preloadImage(variantImageSrc, variantImageSrcset, variantImageSizes);
    };

    class ProductForm extends HTMLElement {
        constructor() {
            super();
            this.sectionId = this.getAttribute('section-id');
            this.elForm = null;
            this.elSection = null;
            this.elMedia = null;
            this.elTitle = null;
            this.elTitleColor = null;
            this.elPrice = null;
            this.elCap = null;
            this.elFinalSale = null;
            this.elOptionValues = null;
            this.elLegacySelector = null;
            this.elPublicationBadge = null;
            this.elDescription = null;
            this.elStickyAtcImage = null;
            this.preloadedSwatches = new WeakSet();
            this.variantHtmlCache = new Map();
            this.variantCacheKeys = [];
            this.prefetchingUrls = new Set();
            this.abortController = null;
            this.sectionsAbortController = null;
            this.finalSale = false;
            this.personalizable = false;
            this.isBundleForm = false;
            this.secondaryOptionCount = 0;
            this.submitStateTimeout = null;
        }

        connectedCallback() {
            this.secondaryOptionCount = Number(this.dataset.secondaryOptions) || 0;
            this.bind();
            requestIdle(() => {
                if (this.isBundleForm) {
                    this.#preloadBundleVariantImages();
                } else {
                    this.#preloadAllFeatured();
                    this.#preloadAllStickyAtcImages();
                }
            });
        }

        bind() {
            this.elForm = this.querySelector('form');
            this.isBundleForm = this.elForm?.classList.contains('product-form--bundle');
            this.elSection = this.closest('section');
            this.elMedia = this.elSection && this.elSection.querySelector('product-media');
            this.elTitle = this.elSection && this.elSection.querySelectorAll('[data-title]');
            this.elTitleColor = this.elSection && this.elSection.querySelectorAll('[data-title-color]');
            this.elStickyAtcImage = this.elSection && this.elSection.querySelector('[data-sticky-atc-image] img');
            this.elPrice = this.elSection?.querySelectorAll('.product-hero--price [data-price], .sticky-atc--product-price [data-price]') ?? [];
            this.elCap = this.elSection?.querySelectorAll('.product-hero--price [data-cap], .sticky-atc--product-price [data-cap]') ?? [];
            this.elFinalSale = this.elSection && this.elSection.querySelector('[data-final-sale]');
            this.elOptionValues = this.querySelectorAll('[data-option-value]');
            this.elLegacySelector = this.querySelector('[data-legacy-selector]');
            this.elPublicationBadge = this.elSection && this.elSection.querySelector('[data-publication-badge]');
            this.elDescription = this.elSection && this.elSection.querySelector('.js-productDescription');

            this.#normalizeColorPathUrl();
            this.#syncInitialOptionSelection();

            const elCheckedOptionValue = this.querySelector('[data-option-value]:checked')
                ?? this.querySelector('[data-option-value]');
            this.finalSale = elCheckedOptionValue?.dataset.variantFinalSale === 'true';
            this.personalizable = elCheckedOptionValue?.dataset.variantPersonalizable === 'true';

            for (const elOptionValue of this.elOptionValues) {
                elOptionValue.addEventListener('change', this.handleOptionValueChange.bind(this));

                const elOption = elOptionValue.closest('[data-option]');
                if (!elOption || elOptionValue.disabled) continue;

                if (this.isBundleForm) {
                    if (!elOptionValue.dataset.variantImageSrc) continue;

                    const onHover = () => preloadVariantImage(elOptionValue);
                    elOptionValue.addEventListener('mouseenter', onHover, { once: true });
                    elOptionValue.addEventListener('focus', onHover, { once: true });

                    const elLabel = elOption.querySelector('label');
                    if (elLabel) {
                        elLabel.addEventListener('mouseenter', onHover, { once: true });
                    }

                    continue;
                }

                const onHover = () => {
                    this.#preloadFullVariant(elOption);
                    preloadVariantImage(elOptionValue);

                    if (this.#needsSectionFetch(elOptionValue)) {
                        this.#prefetchVariantHtml(this.#buildFetchUrlForOption(elOptionValue));
                    }
                };
                elOptionValue.addEventListener('mouseenter', onHover, { once: true });
                elOptionValue.addEventListener('focus', onHover, { once: true });

                const elLabel = elOption.querySelector('label');
                if (elLabel) {
                    elLabel.addEventListener('mouseenter', onHover, { once: true });
                }
            }

            this.elLegacySelector && this.elLegacySelector.addEventListener('change', this.switchProduct.bind(this));
            this.elForm && this.elForm.addEventListener('submit', this.addToCart.bind(this));
        }

        switchProduct(ev) {
            const elLegacySelector = ev.currentTarget;
            const productUrl = elLegacySelector.value;
            const elInput = elLegacySelector.querySelector('input[type="radio"]:checked');

            this.elTitle?.forEach(el => el.textContent = elInput.dataset.productTitle);

            window.history.replaceState({}, '', productUrl);
            this.#resetSubmitState(elInput);
            this.#closeComparisonPopup();
            this.#syncComparisonToggle(elInput);
            this.updateVariantUi(elInput);
            this.fetchVariant(productUrl, { syncRegions: true });
            this.#syncProductSections(productUrl);
            this.#scrollToTop();
        }

        #scrollToTop() {
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            window.scrollTo({
                top: 0,
                behavior: prefersReducedMotion ? 'auto' : 'smooth',
            });
        }

        handleOptionValueChange(ev) {
            const elOptionValue = ev.currentTarget;
            const { productUrl } = elOptionValue.dataset;

            const elSelectedOptionValues = this.querySelectorAll('[data-option-value]:checked');
            const selectedOptionValues = [...elSelectedOptionValues].map(el => el.dataset.optionValue);
            const selectedOptions = [...elSelectedOptionValues].map(el => ({
                name: el.name,
                value: el.value,
            }));
            const params = selectedOptionValues.length > 0 ? `option_values=${selectedOptionValues.join(',')}` : '';
            const url = `${productUrl}?section_id=${this.sectionId}&${params}`;
            const variantUrl = productUrl || window.location.pathname;

            window.history.replaceState({}, '', variantUrl);
            this.#resetSubmitState(elOptionValue);

            const needsFetch = this.#needsSectionFetch(elOptionValue);
            this.updateVariantUi(elOptionValue, { deferMediaTags: needsFetch });

            if (!needsFetch) {
                return;
            }

            this.fetchVariant(url, { selectedOptions });
        }

        #needsSectionFetch(elOptionValue) {
            if (this.isBundleForm) return true;
            if (this.secondaryOptionCount === 0) return false;

            return Boolean(elOptionValue.closest('.product-form--swatch'));
        }

        addToCart(ev) {
            ev.preventDefault();
            const elButtons = this.elSection?.querySelectorAll('[data-product-form-submit] .product-form--submit-button') ?? [];
            const elStatuses = this.elSection?.querySelectorAll('[data-product-form-submit] [data-status]') ?? [];
            const addingMessage = `<span>Adding <span class="visually-hidden">to cart</span>...</span>`;
            const errorMessage = `<span>Error<span class="visually-hidden"> adding to cart</span></span>`;
            const successMessage = `<span>Added to cart</span>`;
            const elGiftCard = this.elForm?.querySelector('product-gift-card');

            if (elGiftCard) {
                const { valid } = elGiftCard.validate();
                if (!valid) {
                    this.classList.add('error');
                    for (const elStatus of elStatuses) {
                        elStatus.innerHTML = errorMessage;
                    }
                    setTimeout(() => {
                        this.classList.remove('error');
                        for (const elStatus of elStatuses) {
                            elStatus.innerHTML = '';
                        }
                    }, 5000);
                    return;
                }
            }

            const cartData = {
                id: Number(this.elForm.querySelector('[name="id"]').value),
                quantity: 1,
                properties: {
                    ...(this.finalSale ? { '_final-sale': true, '_LPROP': 'Final Sale' } : {}),
                    ...(this.personalizable ? { '_personalizable': true } : {}),
                    ...(elGiftCard ? elGiftCard.getPropertiesForCart() : {}),
                }
            }

            this.classList.add("adding");
            for (const elStatus of elStatuses) {
                elStatus.innerHTML = addingMessage;
            }
            for (const elButton of elButtons) {
                elButton.disabled = true;
            }

            window.addToCart(cartData)
                .then((response) => {
                    this.classList.remove("adding");
                    if (response.status) {
                        this.classList.add("error");
                        elGiftCard?.displayApiErrors(response);
                        for (const elStatus of elStatuses) {
                            elStatus.innerHTML = errorMessage;
                        }
                    } else {
                        this.classList.add("success");
                        elGiftCard?.reset();
                        for (const elStatus of elStatuses) {
                            elStatus.innerHTML = successMessage;
                        }
                    }
                    this.submitStateTimeout = setTimeout(() => {
                        this.classList.remove("error");
                        this.classList.remove("success");
                        for (const elButton of elButtons) {
                            elButton.disabled = false;
                        }
                        this.#syncSubmitFromForm();
                        setTimeout(() => {
                            for (const elStatus of elStatuses) {
                                elStatus.innerHTML = "";
                            }
                        }, 300);
                        this.submitStateTimeout = null;
                    }, 5000);
                });
        }

        #resetSubmitState(elOptionValue) {
            if (this.submitStateTimeout) {
                clearTimeout(this.submitStateTimeout);
                this.submitStateTimeout = null;
            }

            if (!this.classList.contains('adding')
                && !this.classList.contains('success')
                && !this.classList.contains('error')) {
                return;
            }

            this.classList.remove('adding', 'success', 'error');

            const elStatuses = this.elSection?.querySelectorAll('[data-product-form-submit] [data-status]') ?? [];

            for (const elStatus of elStatuses) {
                elStatus.innerHTML = '';
            }

            if (elOptionValue) {
                this.#syncSubmitButtons(elOptionValue);
            } else {
                this.#syncSubmitFromForm();
            }
        }

        fetchVariant(url, { syncRegions = false, selectedOptions = null } = {}) {
            if (!syncRegions) {
                const cachedHtml = this.#getFromVariantCache(url);

                if (cachedHtml) {
                    this.#applyVariantHtml(cachedHtml, { syncRegions, selectedOptions });
                    return;
                }
            }

            this.#setLoadingState(true);

            this.abortController?.abort();
            this.abortController = new AbortController();

            fetch(url, { signal: this.abortController.signal })
                .then(response => response.text())
                .then(responseText => {
                    if (!syncRegions) {
                        this.#setVariantCache(url, responseText);
                    }

                    this.#applyVariantHtml(responseText, { syncRegions, selectedOptions });
                })
                .catch(error => {
                    if (error.name === 'AbortError') return;
                    this.#setLoadingState(false);
                    console.error('product-form fetch failed', error);
                });
        }

        #applyVariantHtml(responseText, { syncRegions = false, selectedOptions = null } = {}) {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const elSection = html.querySelector(`#shopify-section-${this.sectionId}`);

            if (!elSection) {
                this.#setLoadingState(false);
                return;
            }

            const elExistingWidget = this.querySelector('[data-product-form-actions] .yotpo-widget-instance');
            const preservedYotpoWidget = elExistingWidget?.childElementCount > 0
                ? elExistingWidget
                : null;
            const yotpoWidgetHeight = preservedYotpoWidget ? 0 : this.#captureYotpoWidgetHeight();

            const elForm = elSection.querySelector('product-form');
            elForm && (this.innerHTML = elForm.innerHTML);

            if (preservedYotpoWidget) {
                const elNewWidget = this.querySelector('[data-product-form-actions] .yotpo-widget-instance');

                if (elNewWidget) {
                    elNewWidget.replaceWith(preservedYotpoWidget);
                }
            }

            const elYotpoWidget = preservedYotpoWidget
                ?? this.#applyYotpoWidgetHeight(yotpoWidgetHeight);

            if (this.isBundleForm) {
                this.#syncHeroPrice(elSection);
            }

            if (syncRegions) {
                this.#syncRegion(elSection, '[data-gwp-region]');
                this.#syncRegion(elSection, '[data-description-region]');
                this.#syncRegion(elSection, '[data-comparison-content]');
                window.initLazyLoad?.();
            }

            this.#syncMediaTagsFromSection(elSection);
            this.#syncPromotionFromSection(elSection);
            this.#setLoadingState(false);
            this.bind();

            if (syncRegions) {
                const elLegacyChecked = this.elLegacySelector?.querySelector('input[type="radio"]:checked');
                if (elLegacyChecked) {
                    this.#syncComparisonToggle(elLegacyChecked);
                }
            }

            if (selectedOptions?.length) {
                this.#restoreSelectedOptions(selectedOptions);
            }

            const elCheckedOptionValue = this.querySelector('[data-option-value]:checked');

            if (elCheckedOptionValue) {
                this.finalSale = elCheckedOptionValue.dataset.variantFinalSale === 'true';
                this.personalizable = elCheckedOptionValue.dataset.variantPersonalizable === 'true';
                this.#syncSubmitButtons(elCheckedOptionValue);
                this.#syncPersonalizationButton(elCheckedOptionValue);
                this.#syncVariantId(elCheckedOptionValue);
                this.#syncWaitlistUi(elCheckedOptionValue);
                this.#syncYotpoWidget(elCheckedOptionValue);

                if (!preservedYotpoWidget) {
                    this.#reinitYotpoWidget(elYotpoWidget);
                }
            } else {
                this.#syncSubmitFromForm();
            }

            if (this.isBundleForm) {
                requestIdle(() => this.#preloadBundleVariantImages());
            }
        }

        #buildFetchUrlForOption(elOptionValue) {
            const { productUrl } = elOptionValue.dataset;
            const groupNames = [...new Set(
                [...this.querySelectorAll('[data-option-value]')].map(el => el.name),
            )];
            const selectedOptionValues = [];

            for (const groupName of groupNames) {
                const elSelected = groupName === elOptionValue.name
                    ? elOptionValue
                    : this.querySelector(`input[name="${CSS.escape(groupName)}"][data-option-value]:checked`);

                if (elSelected?.dataset.optionValue) {
                    selectedOptionValues.push(elSelected.dataset.optionValue);
                }
            }

            const params = selectedOptionValues.length > 0
                ? `option_values=${selectedOptionValues.join(',')}`
                : '';

            return `${productUrl}?section_id=${this.sectionId}&${params}`;
        }

        #getFromVariantCache(url) {
            if (!this.variantHtmlCache.has(url)) return null;

            const index = this.variantCacheKeys.indexOf(url);

            if (index > -1) {
                this.variantCacheKeys.splice(index, 1);
                this.variantCacheKeys.push(url);
            }

            return this.variantHtmlCache.get(url);
        }

        #setVariantCache(url, html) {
            if (this.variantHtmlCache.has(url)) {
                const index = this.variantCacheKeys.indexOf(url);

                if (index > -1) {
                    this.variantCacheKeys.splice(index, 1);
                }
            } else if (this.variantCacheKeys.length >= VARIANT_CACHE_MAX) {
                const oldestUrl = this.variantCacheKeys.shift();
                this.variantHtmlCache.delete(oldestUrl);
            }

            this.variantCacheKeys.push(url);
            this.variantHtmlCache.set(url, html);
        }

        #prefetchVariantHtml(url) {
            if (this.variantHtmlCache.has(url) || this.prefetchingUrls.has(url)) return;

            this.prefetchingUrls.add(url);

            fetch(url)
                .then(response => response.text())
                .then(responseText => {
                    this.#setVariantCache(url, responseText);
                })
                .catch(() => {})
                .finally(() => {
                    this.prefetchingUrls.delete(url);
                });
        }

        #setLoadingState(isLoading) {
            const elSubmitRegions = this.elSection?.querySelectorAll('[data-product-form-submit]') ?? [];
            const loadingLabel = elSubmitRegions[0]?.dataset.loadingLabel ?? 'Loading';

            if (isLoading) {
                this.classList.add('loading');
            } else {
                this.classList.remove('loading');
            }

            for (const elRegion of elSubmitRegions) {
                const elButton = elRegion.querySelector('.product-form--submit-button');

                if (!elButton) continue;

                if (isLoading) {
                    elButton.setAttribute('aria-busy', 'true');
                    elButton.setAttribute('aria-label', loadingLabel);
                } else {
                    elButton.removeAttribute('aria-busy');
                    elButton.removeAttribute('aria-label');
                }
            }
        }

        updateVariantUi(elOptionValue, { deferMediaTags = false } = {}) {
            if (this.isBundleForm) {
                this.#updateBundleComponentUi(elOptionValue);
                this.#updateBundleHeroPrice(elOptionValue);
                this.#updateStickyAtcImage(elOptionValue, elOptionValue.closest('[data-option]'));
                this.#syncSubmitButtons(elOptionValue);
                return;
            }

            const elSwatch = elOptionValue.closest('[data-option]');
            const elMedia = elSwatch && elSwatch.querySelector('template[data-variant-slides]');
            const elDescription = elSwatch && elSwatch.querySelector('template[data-variant-description]');
            const { optionValueTitle, variantPrice, variantCap, variantFinalSale, variantDiscount, variantPublication } = elOptionValue.dataset;
            const elProductMedia = this.#getMedia();

            if (elMedia && elProductMedia && typeof elProductMedia.setVariantMedia === 'function') {
                elProductMedia.setVariantMedia(elMedia);
            }

            if (!deferMediaTags) {
                this.#applyMediaTags(elOptionValue);
            }

            this.#applyPromotion(elOptionValue);

            this.elPublicationBadge && this.elPublicationBadge.toggleAttribute('hidden', !variantPublication);
            this.elPublicationBadge && variantPublication && (this.elPublicationBadge.innerHTML = JSON.parse(variantPublication));
            if (elOptionValue.closest('.product-form--swatch')) {
                this.elTitleColor?.forEach(el => el.textContent = ` in ${optionValueTitle} `);
            } else if (elOptionValue.dataset.productTitle && optionValueTitle) {
                this.elTitleColor?.forEach(el => el.textContent = ` in ${optionValueTitle} `);
            }

            this.#syncBreadcrumbSchema(elOptionValue);

            this.#updateStickyAtcImage(elOptionValue, elSwatch);
            this.#syncSubmitButtons(elOptionValue);
            this.#syncPersonalizationButton(elOptionValue);
            this.#syncPersonalizationData(elOptionValue);
            this.#syncVariantId(elOptionValue);
            this.#syncWaitlistUi(elOptionValue);
            this.#syncYotpoWidget(elOptionValue);
            this.elPrice.forEach(el => el.textContent = variantPrice);
            this.elCap.forEach(el => {
                el.toggleAttribute('hidden', !variantDiscount);

                if (variantDiscount) {
                    el.textContent = variantCap;
                } else {
                    el.textContent = '';
                }
            });
            this.elFinalSale && (this.elFinalSale.hidden = variantFinalSale === 'false');
            this.elDescription && elDescription && (this.elDescription.innerHTML = elDescription.innerHTML);
            this.#syncShopPayAmount(elOptionValue);
        }

        #syncBreadcrumbSchema(elOptionValue) {
            const elTitle = this.elSection?.querySelector('[data-breadcrumb-title]');
            const elSchema = this.elSection?.querySelector('[data-breadcrumb-schema]');

            if (!elTitle || !elSchema) return;

            try {
                const schema = JSON.parse(elSchema.textContent);
                const items = schema.itemListElement;

                if (!Array.isArray(items) || items.length === 0) return;

                const lastItem = items[items.length - 1];
                lastItem.name = elTitle.textContent.replace(/\s+/g, ' ').trim();

                const productUrl = elOptionValue?.dataset?.productUrl;

                if (productUrl) {
                    lastItem.item = new URL(productUrl, window.location.origin).href;
                }

                elSchema.textContent = JSON.stringify(schema);
            } catch (error) {
                console.error('product-form breadcrumb schema sync failed', error);
            }
        }

        #syncShopPayAmount(elOptionValue) {
            const { variantPriceCents } = elOptionValue.dataset;
            const elPayments = this.elSection?.querySelector('.pdp__payments-amnt');

            if (!elPayments || !variantPriceCents) return;

            if (typeof window.formatMoney === 'function') {
                elPayments.textContent = window.formatMoney(Number(variantPriceCents) / 4, 2);
            }
        }

        #updateStickyAtcImage(elOptionValue, elOption) {
            if (!this.elStickyAtcImage) return;

            const { variantImage, variantImageSrc, variantImageSrcset, variantImageSizes } = elOptionValue.dataset;

            preloadVariantImage(elOptionValue);

            if (variantImage) {
                this.elStickyAtcImage.outerHTML = JSON.parse(variantImage);
                this.elStickyAtcImage = this.elSection.querySelector('[data-sticky-atc-image] img');
                this.#prepareStickyAtcImage(this.elStickyAtcImage);
                return;
            }

            const elTemplateImage = elOption?.querySelector('template[data-variant-slides]')?.content.querySelector('img');
            const src = variantImageSrc || elTemplateImage?.getAttribute('src');
            if (!src) return;

            this.#prepareStickyAtcImage(this.elStickyAtcImage);
            this.elStickyAtcImage.src = src;

            const srcset = variantImageSrcset || elTemplateImage?.getAttribute('srcset');
            if (srcset) {
                this.elStickyAtcImage.srcset = srcset;
            } else {
                this.elStickyAtcImage.removeAttribute('srcset');
            }

            const sizes = variantImageSizes || elTemplateImage?.getAttribute('sizes');
            if (sizes) {
                this.elStickyAtcImage.sizes = sizes;
            } else {
                this.elStickyAtcImage.removeAttribute('sizes');
            }
        }

        #getCheckedOptionValue() {
            return this.querySelector('[data-option-value]:checked')
                ?? this.querySelector('[data-option-value]');
        }

        #normalizeColorPathUrl() {
            const { colorPathFallbackUrl } = this.dataset;

            if (colorPathFallbackUrl) {
                window.history.replaceState({}, '', colorPathFallbackUrl);
            }
        }

        #syncColorPathSelection() {
            const pathParts = window.location.pathname.split('/').filter(Boolean);
            const productsIndex = pathParts.indexOf('products');

            if (productsIndex < 0 || !pathParts[productsIndex + 2]) return null;

            const colorPath = `/${pathParts.slice(productsIndex, productsIndex + 3).join('/')}`.replace(/\/$/, '');

            for (const elOptionValue of this.elOptionValues) {
                const { productUrl } = elOptionValue.dataset;

                if (!productUrl) continue;

                const elProductUrl = new URL(productUrl, window.location.origin);
                const optionPath = elProductUrl.pathname.replace(/\/$/, '');

                if (optionPath === colorPath) {
                    elOptionValue.checked = true;
                    return elOptionValue;
                }
            }

            return null;
        }

        #syncInitialOptionSelection() {
            const url = new URL(window.location.href);
            const variantParam = url.searchParams.get('variant');

            if (variantParam) {
                this.#checkOptionValuesByVariantId(variantParam);
            }

            if (!variantParam) {
                this.#syncColorPathSelection();
            }

            const optionValuesParam = url.searchParams.get('option_values');

            if (optionValuesParam && !this.querySelector('[data-option-value]:checked')) {
                this.#restoreSelectedOptionValues(optionValuesParam.split(','));
            }

            const elCheckedFromPath = this.querySelector('[data-option-value]:checked');

            if (elCheckedFromPath) {
                this.updateVariantUi(elCheckedFromPath);
            }

            if (!this.querySelector('[data-option-value]:checked')) {
                const elVariantId = this.elForm?.querySelector('[name="id"]');

                if (elVariantId?.value) {
                    this.#checkOptionValuesByVariantId(elVariantId.value);
                }
            }

            this.#syncOptionSelectionState();
        }

        #checkOptionValuesByVariantId(variantId) {
            for (const elOptionValue of this.querySelectorAll(`[data-variant-id="${CSS.escape(variantId)}"]`)) {
                elOptionValue.checked = true;
            }
        }

        #syncOptionSelectionState() {
            const elCheckedOptionValue = this.querySelector('[data-option-value]:checked');
            const elVariantId = this.elForm?.querySelector('[name="id"]');

            if (!elCheckedOptionValue || !elVariantId) return;

            this.#syncPersonalizationButton(elCheckedOptionValue);
            this.#syncWaitlistUi(elCheckedOptionValue);

            if (elCheckedOptionValue.dataset.variantId === elVariantId.value) return;

            this.finalSale = elCheckedOptionValue.dataset.variantFinalSale === 'true';
            this.personalizable = elCheckedOptionValue.dataset.variantPersonalizable === 'true';
            this.#syncSubmitButtons(elCheckedOptionValue);
            this.#syncVariantId(elCheckedOptionValue);
            this.#syncYotpoWidget(elCheckedOptionValue);
        }

        #restoreSelectedOptionValues(optionValueIds) {
            for (const optionValueId of optionValueIds) {
                if (!optionValueId) continue;

                const elOptionValue = this.querySelector(`[data-option-value="${CSS.escape(optionValueId)}"]`);

                if (elOptionValue) {
                    elOptionValue.checked = true;
                }
            }
        }

        #restoreSelectedOptions(selectedOptions) {
            for (const { name, value } of selectedOptions) {
                if (!name || value == null) continue;

                for (const elOptionValue of this.querySelectorAll(`input[name="${CSS.escape(name)}"]`)) {
                    elOptionValue.checked = elOptionValue.value === value;
                }
            }
        }

        #syncVariantId(elOptionValue) {
            const elVariantId = this.elForm?.querySelector('[name="id"]');
            const { variantId } = elOptionValue.dataset;

            if (elVariantId && variantId) {
                elVariantId.value = variantId;
            }

            const elGiftCard = this.elForm?.querySelector('product-gift-card');
            if (elGiftCard && variantId) {
                elGiftCard.updateVariantId(variantId);
            }

            this.finalSale = elOptionValue.dataset.variantFinalSale === 'true';
            this.personalizable = elOptionValue.dataset.variantPersonalizable === 'true';
        }

        #syncSubmitButtons(elOptionValue) {
            const elSubmitRegions = this.elSection?.querySelectorAll('[data-product-form-submit]') ?? [];
            const isAvailable = elOptionValue.dataset.variantAvailable !== 'false';

            for (const elRegion of elSubmitRegions) {
                const elButton = elRegion.querySelector('.product-form--submit-button');
                const { addLabel, soldOutLabel } = elRegion.dataset;

                if (!elButton) continue;

                elButton.disabled = !isAvailable;
                elButton.textContent = isAvailable ? addLabel : soldOutLabel;
            }
        }

        #syncPersonalizationButton(elOptionValue) {
            const elTriggers = this.elForm?.querySelectorAll('[data-personalization-trigger]') ?? [];
            if (!elTriggers.length) return;

            const isAvailable = elOptionValue.dataset.variantAvailable !== 'false';
            const isPersonalizable = elOptionValue.dataset.variantPersonalizable === 'true';

            for (const elTrigger of elTriggers) {
                elTrigger.toggleAttribute('hidden', !(isAvailable && isPersonalizable));
            }
        }

        #syncPersonalizationData(elOptionValue) {
            const elModal = this.querySelector('personalization-modal');

            if (!elModal || typeof elModal.updateData !== 'function') {
                return;
            }

            const elOption = elOptionValue.closest('[data-option]');
            const elTemplate = elOption?.querySelector('template[data-personalization-data]');

            if (!elTemplate?.innerHTML.trim()) {
                return;
            }

            try {
                elModal.updateData(JSON.parse(elTemplate.innerHTML.trim()));
            } catch (error) {
                console.error('product-form personalization sync failed', error);
            }
        }

        #syncWaitlistUi(elOptionValue) {
            if (!this.hasAttribute('data-waitlist-enabled')) return;

            const isAvailable = elOptionValue.dataset.variantAvailable !== 'false';
            const elActions = this.elForm?.querySelector('[data-product-form-actions]');
            const elWaitlist = this.querySelector('[data-product-form-waitlist]');

            elActions?.toggleAttribute('hidden', !isAvailable);
            elWaitlist?.toggleAttribute('hidden', isAvailable);

            const { variantId } = elOptionValue.dataset;

            if (elWaitlist && variantId) {
                elWaitlist.setAttribute('variant-id', variantId);
            }
        }

        #syncSubmitFromForm() {
            const elPrimaryButton = this.elForm?.querySelector('[data-product-form-submit] .product-form--submit-button');
            const elSubmitRegions = this.elSection?.querySelectorAll('[data-product-form-submit]') ?? [];

            if (!elPrimaryButton) return;

            for (const elRegion of elSubmitRegions) {
                const elButton = elRegion.querySelector('.product-form--submit-button');

                if (!elButton || elButton === elPrimaryButton) continue;

                elButton.disabled = elPrimaryButton.disabled;
                elButton.textContent = elPrimaryButton.textContent.trim();
            }
        }

        #prepareStickyAtcImage(elImage) {
            if (!elImage) return;

            elImage.loading = 'eager';
            elImage.classList.add('in-view');
        }

        #preloadAllStickyAtcImages() {
            for (const elOptionValue of this.elOptionValues) {
                preloadVariantImage(elOptionValue);
            }
        }

        #preloadAllFeatured() {
            for (const elOptionValue of this.elOptionValues) {
                const elOption = elOptionValue.closest('[data-option]');
                if (!elOption) continue;

                const elTemplate = elOption.querySelector('template[data-variant-slides]');
                if (!elTemplate) continue;

                const elFirstImage = elTemplate.content.querySelector('img');
                if (!elFirstImage) continue;

                preloadImage(
                    elFirstImage.getAttribute('src'),
                    elFirstImage.getAttribute('srcset'),
                    elFirstImage.getAttribute('sizes'),
                );
            }
        }

        #preloadBundleVariantImages() {
            for (const elOptionValue of this.elOptionValues) {
                if (elOptionValue.checked || elOptionValue.disabled) continue;
                if (!elOptionValue.dataset.variantImageSrc) continue;

                preloadVariantImage(elOptionValue);
            }
        }

        #updateBundleComponentUi(elOptionValue) {
            const elComponent = elOptionValue.closest('.product-form--bundle-component');
            if (!elComponent) return;

            const { variantPrice, variantCap, variantDiscount, variantImage, componentUrl } = elOptionValue.dataset;
            const elImage = elComponent.querySelector('[data-component-image]');
            const elPrice = elComponent.querySelector('[data-component-price]');
            const elCap = elComponent.querySelector('[data-component-cap]');
            const elUrl = elComponent.querySelector('[data-url]');

            preloadVariantImage(elOptionValue);

            if (elImage && variantImage) {
                elImage.innerHTML = JSON.parse(variantImage);
                window.initLazyLoad?.();
            }

            elPrice && (elPrice.textContent = variantPrice);
            elCap && elCap.toggleAttribute('hidden', !variantDiscount);
            elCap && variantDiscount && (elCap.textContent = variantCap);
            elUrl && componentUrl && (elUrl.href = componentUrl);
        }

        #updateBundleHeroPrice(elOptionValue) {
            const { bundleVariantPrice, bundleVariantCap } = elOptionValue.dataset;

            if (!bundleVariantPrice) return;

            this.elPrice?.forEach(el => el.textContent = bundleVariantPrice);
            this.elCap?.forEach(el => el.textContent = bundleVariantCap || '');
        }

        #syncHeroPrice(elSection) {
            const elFetchedPrice = elSection.querySelector('.product-hero--price [data-price]');
            const elFetchedCap = elSection.querySelector('.product-hero--price [data-cap]');

            elFetchedPrice && this.elPrice?.forEach(el => el.textContent = elFetchedPrice.textContent);
            elFetchedCap && this.elCap?.forEach(el => el.textContent = elFetchedCap.textContent);
        }

        #getMedia() {
            if (!this.elMedia || !this.elMedia.isConnected) {
                this.elMedia = this.elSection?.querySelector('product-media');
            }

            return this.elMedia;
        }

        #applyMediaTags(elOptionValue) {
            const elProductMedia = this.#getMedia();
            if (!elProductMedia || typeof elProductMedia.setVariantTags !== 'function') return;

            elProductMedia.setVariantTags({
                discount: elOptionValue.dataset.variantDiscount,
                freeShipping: Boolean(elOptionValue.dataset.variantFreeShipping),
            });
        }

        #syncMediaTagsFromSection(elSection) {
            const elProductMedia = this.#getMedia();
            if (!elProductMedia || typeof elProductMedia.setVariantTags !== 'function') return;

            const elFetchedTags = elSection.querySelector('[data-media-tags]');
            if (!elFetchedTags) return;

            const elFetchedSaleTag = elFetchedTags.querySelector('[data-sale-tag]');
            const elFetchedShippingTag = elFetchedTags.querySelector('[data-shipping-tag]');

            elProductMedia.setVariantTags({
                discount: elFetchedSaleTag && !elFetchedSaleTag.hasAttribute('hidden')
                    ? elFetchedSaleTag.textContent.trim()
                    : '',
                freeShipping: Boolean(elFetchedShippingTag && !elFetchedShippingTag.hasAttribute('hidden')),
            });
        }

        #syncPromotionFromSection(elSection) {
            const elFetched = elSection.querySelector('[data-promotion-region]');
            const elLive = this.elSection?.querySelector('[data-promotion-region]');
            if (!elFetched || !elLive) return;
            if (elFetched.dataset.promotionProduct === elLive.dataset.promotionProduct) return;

            this.#setPromotionHtml(elLive, elFetched.innerHTML, elFetched.dataset.promotionProduct);
        }

        #applyPromotion(elOptionValue) {
            const elRegion = this.elSection?.querySelector('[data-promotion-region]');
            if (!elRegion) return;

            const elTemplate = elOptionValue.closest('[data-option]')?.querySelector('template[data-variant-promotion]');
            if (!elTemplate) return;

            const nextProductId = elTemplate.dataset.promotionProduct;
            if (nextProductId && nextProductId === elRegion.dataset.promotionProduct) return;

            this.#setPromotionHtml(elRegion, elTemplate.innerHTML, nextProductId);
        }

        #setPromotionHtml(elRegion, html, productId) {
            elRegion.innerHTML = html;

            if (productId) {
                elRegion.dataset.promotionProduct = productId;
            }
        }

        #closeComparisonPopup() {
            const elModal = this.elSection?.querySelector('#popup-comparison-table');

            if (elModal?.getAttribute('open') === 'true' && typeof elModal.close === 'function') {
                elModal.close();
            }
        }

        #syncComparisonToggle(elInput) {
            const elToggle = this.querySelector('[data-comparison-toggle]');

            if (!elToggle) return;

            const hasChart = elInput.dataset.hasComparisonChart === 'true';
            const buttonText = elInput.dataset.comparisonButtonText;

            elToggle.toggleAttribute('hidden', !hasChart);

            if (hasChart && buttonText) {
                elToggle.textContent = buttonText;
            }
        }

        #syncRegion(elSection, selector) {
            const elFetched = elSection.querySelector(selector);
            const elLive = this.elSection?.querySelector(selector);

            if (!elLive) return;

            if (!elFetched) {
                elLive.innerHTML = '';
                return;
            }

            elLive.innerHTML = elFetched.innerHTML;
        }

        #ensureStylesheet(href) {
            if (!href) return;

            const isLoaded = [...document.querySelectorAll('link[rel="stylesheet"]')]
                .some(el => el.href === href);

            if (isLoaded) return;

            const elLink = document.createElement('link');
            elLink.rel = 'stylesheet';
            elLink.href = href;
            document.head.appendChild(elLink);
        }

        #parseSectionElement(html) {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const elNew = doc.body.firstElementChild;

            if (!elNew) return null;

            for (const el of elNew.querySelectorAll('link[rel="stylesheet"]')) {
                this.#ensureStylesheet(el.href);
                el.remove();
            }

            for (const el of elNew.querySelectorAll('script')) {
                el.remove();
            }

            return elNew;
        }

        #removeSectionsAfterHero() {
            let elNext = this.elSection?.nextElementSibling;

            while (elNext) {
                const elRemove = elNext;
                elNext = elNext.nextElementSibling;
                elRemove.remove();
            }
        }

        #getSectionsAfterHero(html) {
            const elFetchedHero = html.querySelector(`#shopify-section-${this.sectionId}`);
            const sections = [];
            let elNext = elFetchedHero?.nextElementSibling;

            while (elNext) {
                if (elNext.classList.contains('shopify-section')) {
                    sections.push(elNext);
                }

                elNext = elNext.nextElementSibling;
            }

            return sections;
        }

        #insertSectionsAfterHero(sections) {
            let elAnchor = this.elSection;

            for (const elFetched of sections) {
                const elNew = this.#parseSectionElement(elFetched.outerHTML);
                if (!elNew) continue;

                elAnchor.insertAdjacentElement('afterend', elNew);
                elAnchor = elNew;
            }
        }

        #syncYotpoWidget(elOptionValue) {
            const elActions = this.elForm?.querySelector('[data-product-form-actions]');
            if (!elActions || elActions.hasAttribute('hidden')) return;

            const { variantId } = elOptionValue.dataset;
            const elWidget = elActions.querySelector('.yotpo-widget-instance');
            const elVariantId = this.elForm?.querySelector('[name="id"]');

            if (elWidget && variantId) {
                elWidget.dataset.yotpoVariantId = variantId;
            }

            if (!elVariantId) return;

            elVariantId.dispatchEvent(new Event('change', { bubbles: true }));
            elVariantId.dispatchEvent(new Event('input', { bubbles: true }));
        }

        #captureYotpoWidgetHeight() {
            const elWidget = this.elForm?.querySelector('[data-product-form-actions] .yotpo-widget-instance');

            return elWidget?.offsetHeight ?? 0;
        }

        #applyYotpoWidgetHeight(height) {
            const elWidget = this.elForm?.querySelector('[data-product-form-actions] .yotpo-widget-instance');

            if (elWidget && height > 0) {
                elWidget.style.minHeight = `${height}px`;
            }

            return elWidget;
        }

        #releaseYotpoWidgetHeight(elWidget) {
            if (!elWidget) return;

            const release = () => {
                elWidget.style.minHeight = '';
            };

            if (elWidget.childElementCount > 0) {
                release();
                return;
            }

            const observer = new MutationObserver(() => {
                if (elWidget.childElementCount > 0) {
                    release();
                    observer.disconnect();
                }
            });

            observer.observe(elWidget, { childList: true });
            setTimeout(() => {
                observer.disconnect();
                release();
            }, 2000);
        }

        #reinitYotpoWidget(elWidget) {
            const elActions = this.elForm?.querySelector('[data-product-form-actions]');
            if (!elActions || elActions.hasAttribute('hidden')) return;

            elWidget ??= elActions.querySelector('.yotpo-widget-instance');
            if (!elWidget) return;

            this.#refreshThirdPartyWidgets();
            this.#releaseYotpoWidgetHeight(elWidget);
        }

        #refreshThirdPartyWidgets() {
            window.initLazyLoad?.();

            if (typeof window.yotpoWidgetsContainer?.initWidgets === 'function') {
                window.yotpoWidgetsContainer.initWidgets();
            } else if (typeof window.yotpo?.refreshWidgets === 'function') {
                window.yotpo.refreshWidgets();
            } else if (typeof window.yotpo?.initWidgets === 'function') {
                window.yotpo.initWidgets();
            }
        }

        #syncProductSections(productUrl) {
            if (!this.elSection) return;

            this.sectionsAbortController?.abort();
            this.sectionsAbortController = new AbortController();

            fetch(productUrl, { signal: this.sectionsAbortController.signal })
                .then(response => response.text())
                .then(responseText => {
                    const html = new DOMParser().parseFromString(responseText, 'text/html');
                    const sections = this.#getSectionsAfterHero(html);

                    this.#removeSectionsAfterHero();
                    this.#insertSectionsAfterHero(sections);
                    this.#refreshThirdPartyWidgets();
                })
                .catch(error => {
                    if (error.name === 'AbortError') return;
                    console.error('product-form section sync failed', error);
                });
        }

        #preloadFullVariant(elOption) {
            if (this.preloadedSwatches.has(elOption)) return;
            this.preloadedSwatches.add(elOption);

            const elTemplate = elOption.querySelector('template[data-variant-slides]');
            if (!elTemplate) return;

            const elImages = elTemplate.content.querySelectorAll('img');
            for (const elImage of elImages) {
                preloadImage(
                    elImage.getAttribute('src'),
                    elImage.getAttribute('srcset'),
                    elImage.getAttribute('sizes'),
                );
            }
        }
    }

    customElements.define('product-form', ProductForm);
}
